"use client";

import { useEffect, useRef } from "react";

const STREAMING_TEXT_WIPE_MS = 280;
const STREAMING_TEXT_BLOCKED_TAGS = new Set([
  "code",
  "pre",
  "kbd",
  "math",
  "script",
  "style",
]);
const STREAMING_TEXT_ELIGIBLE_TAGS = new Set([
  "p",
  "li",
  "blockquote",
  "td",
  "th",
  "strong",
  "em",
  "del",
  "a",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
]);
const STREAM_STATE_LIMIT = 64;

type StreamingTextRegistryEntry = {
  startedAt: number;
  value: string;
};

export type StreamingTextState = {
  // Length of normalized source already revealed; offsets below it never
  // re-animate, even when a completed markdown construct reflows the tree.
  highWater: number;
  // Source offsets currently mid-wipe, so a re-render resumes the animation
  // via negative delay instead of restarting it.
  registry: Map<number, StreamingTextRegistryEntry>;
};

export function createStreamingTextState(): StreamingTextState {
  return { highWater: 0, registry: new Map() };
}

// Keyed by streamIdentity so a remount mid-stream resumes where the previous
// instance left off instead of replaying the wipe over the whole answer.
const streamStates = new Map<string, StreamingTextState>();

function commitStreamState(identity: string, state: StreamingTextState) {
  streamStates.delete(identity);
  while (streamStates.size >= STREAM_STATE_LIMIT) {
    const oldest = streamStates.keys().next().value;
    if (oldest === undefined) break;
    streamStates.delete(oldest);
  }
  streamStates.set(identity, state);
}

export function useStreamingMarkdownRehypePlugins({
  enabled,
  source,
  streamIdentity,
}: {
  enabled: boolean;
  source: string;
  streamIdentity?: string;
}) {
  const identity = streamIdentity ?? "anonymous-stream";
  const now =
    typeof performance === "undefined" ? Date.now() : performance.now();
  const commitRef = useRef<{ identity: string; state: StreamingTextState } | null>(
    null,
  );
  const plugins = enabled
    ? [
        () => (tree: unknown) => {
          const baseline = streamStates.get(identity) ?? createStreamingTextState();
          const state = applyStreamingTextAnimation(tree, {
            now,
            source,
            state: baseline,
          });
          commitRef.current = { identity, state };
        },
      ]
    : undefined;
  useEffect(() => {
    if (!enabled) {
      streamStates.delete(identity);
      commitRef.current = null;
      return;
    }
    const commit = commitRef.current;
    if (commit && commit.identity === identity) {
      commitStreamState(identity, commit.state);
    }
  });
  return plugins;
}

function isElementNode(node: unknown): node is {
  children?: unknown[];
  tagName?: string;
  type: "element";
} {
  return Boolean(
    node &&
      typeof node === "object" &&
      (node as { type?: unknown }).type === "element",
  );
}

function isParentNode(node: unknown): node is {
  children: unknown[];
  tagName?: string;
} {
  return Boolean(
    node &&
      typeof node === "object" &&
      Array.isArray((node as { children?: unknown }).children),
  );
}

type StreamingTextNode = {
  type: "text";
  value: string;
  position?: { start?: { offset?: number }; end?: { offset?: number } };
};

function isTextNode(node: unknown): node is StreamingTextNode {
  return Boolean(
    node &&
      typeof node === "object" &&
      (node as { type?: unknown }).type === "text" &&
      typeof (node as { value?: unknown }).value === "string",
  );
}

function walkStreamingTextNodes(
  node: unknown,
  parentTag: string | null,
  blocked: boolean,
  visitor: (node: StreamingTextNode) => void,
) {
  if (isTextNode(node)) {
    if (!blocked && parentTag && STREAMING_TEXT_ELIGIBLE_TAGS.has(parentTag)) {
      visitor(node);
    }
    return;
  }
  if (!isParentNode(node)) return;

  const tagName = isElementNode(node) ? node.tagName ?? null : null;
  const nextBlocked =
    blocked || (tagName ? STREAMING_TEXT_BLOCKED_TAGS.has(tagName) : false);
  for (const child of node.children) {
    walkStreamingTextNodes(child, tagName, nextBlocked, visitor);
  }
}

function nodeSourceRange(node: StreamingTextNode) {
  const start = node.position?.start?.offset;
  const end = node.position?.end?.offset;
  if (typeof start !== "number" || typeof end !== "number") return null;
  return { start, end };
}

type StreamingTextRun = {
  text: string;
  startedAt: number | null;
};

function charStartedAt(
  offset: number,
  source: string,
  input: { now: number; highWater: number },
  registry: Map<number, StreamingTextRegistryEntry>,
) {
  const entry = registry.get(offset);
  if (entry) return entry.startedAt;
  if (offset < input.highWater) return null;
  registry.set(offset, { startedAt: input.now, value: source[offset] ?? "" });
  return input.now;
}

function collectNodeRuns(
  node: StreamingTextNode,
  source: string,
  input: { now: number; highWater: number },
  registry: Map<number, StreamingTextRegistryEntry>,
): StreamingTextRun[] {
  const range = nodeSourceRange(node);
  // Without a faithful source mapping the text is treated as already shown:
  // never animating is safe, re-animating shown text is not.
  if (!range) return [{ text: node.value, startedAt: null }];

  const exact =
    range.end - range.start === node.value.length &&
    source.startsWith(node.value, range.start);
  if (!exact) {
    const startedAt =
      registry.get(range.start)?.startedAt ??
      (range.start >= input.highWater ? input.now : null);
    if (startedAt === input.now) {
      registry.set(range.start, {
        startedAt: input.now,
        value: source[range.start] ?? "",
      });
    }
    return [{ text: node.value, startedAt }];
  }

  const runs: StreamingTextRun[] = [];
  for (let index = 0; index < node.value.length; index += 1) {
    const startedAt = charStartedAt(range.start + index, source, input, registry);
    const current = runs.at(-1);
    if (current && current.startedAt === startedAt) {
      current.text += node.value[index] ?? "";
    } else {
      runs.push({ text: node.value[index] ?? "", startedAt });
    }
  }
  return runs;
}

export function applyStreamingTextAnimation(
  tree: unknown,
  input: { now: number; source: string; state: StreamingTextState },
): StreamingTextState {
  const { now, source, state } = input;
  const registry = new Map(state.registry);
  for (const [offset, entry] of registry) {
    if (
      now - entry.startedAt >= STREAMING_TEXT_WIPE_MS ||
      offset >= source.length ||
      source[offset] !== entry.value
    ) {
      registry.delete(offset);
    }
  }

  const runInput = { now, highWater: state.highWater };
  walkStreamingTextNodes(tree, null, false, (node) => {
    const runs = collectNodeRuns(node, source, runInput, registry);
    if (!runs.some((run) => run.startedAt !== null)) return;

    const children = runs.map((run) => {
      if (run.startedAt === null) {
        return { type: "text", value: run.text };
      }
      const ageMs = Math.max(0, now - run.startedAt);
      return {
        type: "element",
        tagName: "span",
        properties: {
          className: ["mlx-stream-text-run"],
          style: [
            `--mlx-stream-delay: -${ageMs}ms`,
            `--mlx-stream-duration: ${STREAMING_TEXT_WIPE_MS}ms`,
          ].join(";"),
        },
        children: [{ type: "text", value: run.text }],
      };
    });
    Object.assign(node, {
      type: "element",
      tagName: "span",
      properties: {},
      children,
    });
  });

  return {
    highWater: Math.max(state.highWater, source.length),
    registry,
  };
}
